import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Mail, Wallet, CheckCircle2, Inbox, AlertTriangle,
  CalendarClock, ChevronRight, XCircle, Image as ImageIcon, ExternalLink,
} from "lucide-react";
import AppLayout from "../../components/AppLayout";
import StatusBadge from "../../components/StatusBadge";
import DueChip from "../../components/DueChip";
import api, {
  formatCurrency, formatDate, API_BASE, getAccessToken, formatMethod,
} from "../../lib/api";
import { toast } from "sonner";
import { useTerms } from "../../lib/BrandContext";
import { useSmartBack } from "../../lib/backNavigation";
import { Button } from "../../components/ui/button";

/**
 * Coach-only per-player Financial Profile.
 *
 * Everything the coach needs to see for one player in ONE screen:
 *   - Profile header (name, team, jersey, parents)
 *   - 5 aggregate totals (Assigned / Paid / Unpaid / To Review / Overdue)
 *   - Full fee ledger — one row per payment_record, chronological (newest first)
 *
 * All heavy lifting (ownership check, totals, hydration) happens server-side
 * in GET /api/players/{id}/ledger, so the page renders from a single fetch.
 */

/* ---------------------------------------------------------------------- */
/* Total pill                                                             */
/* ---------------------------------------------------------------------- */

function TotalPill({ label, amount, tone = "neutral", icon: Icon, testid }) {
  const toneClasses = {
    neutral: "border-ink-100 bg-white text-ink-900",
    paid:    "border-emerald-200 bg-emerald-50 text-emerald-900",
    unpaid:  "border-ink-200 bg-ink-100/50 text-ink-900",
    review:  "border-amber-200 bg-amber-50 text-amber-900",
    overdue: "border-rose-200 bg-rose-50 text-rose-900",
  }[tone] || "border-ink-100 bg-white text-ink-900";
  return (
    <div className={`flex flex-col gap-1 rounded-xl border p-4 ${toneClasses}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide opacity-75">
        {Icon ? <Icon className="h-3.5 w-3.5" aria-hidden="true" /> : null}
        {label}
      </div>
      <div className="font-heading text-xl font-bold sm:text-2xl">{formatCurrency(amount)}</div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Single ledger row (mobile: card, desktop: table row)                   */
/* ---------------------------------------------------------------------- */

function LedgerRow({ r, onReview }) {
  const chargeTitle = r.charge?.title || "Fee";
  const dueDate = r.charge?.due_date || "";
  const hasReceipt = !!r.receipt_path;
  const token = getAccessToken();
  const receiptUrl = hasReceipt
    ? `${API_BASE}/files/${r.receipt_path}${token ? `?auth=${encodeURIComponent(token)}` : ""}`
    : null;
  const isImage = hasReceipt && /\.(png|jpe?g|gif|webp|heic|heif)$/i.test(r.receipt_path);
  const isCanceled = r.status === "canceled";
  const submittedAt = r.submitted_at || r.paid_marked_at;
  const confirmedAt = r.confirmed_at;

  // Amount cascade indicator — when the current record.amount differs from
  // the fee's current amount because this record was confirmed BEFORE an
  // edit, we surface a "modified" tag so the coach understands the audit
  // trail. `charge.amount` is the current fee amount; `r.amount` is what
  // was actually collected on this record.
  const feeAmount = r.charge?.amount;
  const wasModified = feeAmount != null && Number(feeAmount) !== Number(r.amount);

  return (
    <li
      data-testid={`ledger-row-${r.id}`}
      className={`rounded-xl border p-4 ${isCanceled ? "border-ink-100 bg-ink-100/40 opacity-70" : "border-ink-100 bg-white"}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {r.charge?.id ? (
              <Link
                to={`/coach/charges/${r.charge.id}`}
                className="font-semibold text-ink-900 hover:text-brand"
                data-testid={`ledger-row-fee-link-${r.id}`}
              >
                {chargeTitle}
              </Link>
            ) : (
              <span className="font-semibold text-ink-900">{chargeTitle}</span>
            )}
            <StatusBadge status={r.status} />
            {wasModified ? (
              <span
                className="rounded-full border border-ink-200 bg-ink-100/60 px-2 py-0.5 text-xs font-medium text-ink-700"
                title={`Fee amount changed to ${formatCurrency(feeAmount)}. This record was confirmed at the earlier amount and is kept for audit.`}
                data-testid={`ledger-row-modified-${r.id}`}
              >
                Modified
              </span>
            ) : null}
            {isCanceled ? (
              <span className="rounded-full border border-ink-200 bg-ink-100/60 px-2 py-0.5 text-xs font-medium text-ink-500">
                Not counted in totals
              </span>
            ) : null}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-500">
            {dueDate ? (
              <DueChip dueDate={dueDate} chargeStatus={r.charge?.status || "active"} />
            ) : null}
            {r.method ? <span>Method: <span className="font-medium text-ink-700">{formatMethod(r.method)}</span></span> : null}
            {submittedAt ? <span>Submitted: <span className="font-medium text-ink-700">{formatDate(submittedAt)}</span></span> : null}
            {confirmedAt ? <span>Confirmed: <span className="font-medium text-ink-700">{formatDate(confirmedAt)}</span></span> : null}
          </div>
          {r.note ? (
            <p className="mt-2 text-sm text-ink-700"><span className="font-semibold">Parent note:</span> {r.note}</p>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2">
          <div className="font-heading text-lg font-bold text-ink-900">
            {formatCurrency(r.amount)}
          </div>
          {wasModified ? (
            <div className="text-[11px] text-ink-500">
              Fee is now {formatCurrency(feeAmount)}
            </div>
          ) : null}
          {r.status === "submitted" ? (
            <Button
              onClick={(e) => { e.stopPropagation(); onReview(r); }}
              className="h-9 bg-amber-500 px-3 text-sm font-semibold text-white hover:bg-amber-600"
              data-testid={`ledger-row-review-${r.id}`}
            >
              Review
            </Button>
          ) : null}
        </div>
      </div>

      {hasReceipt ? (
        <a
          href={receiptUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-flex items-center gap-3 rounded-lg border border-ink-100 bg-ink-100/40 p-2 text-sm text-ink-700 hover:bg-ink-100"
          data-testid={`ledger-row-receipt-${r.id}`}
        >
          {isImage ? (
            <img
              src={receiptUrl}
              alt="Payment receipt"
              className="h-14 w-14 shrink-0 rounded-md border border-ink-200 object-cover"
              loading="lazy"
            />
          ) : (
            <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-md border border-ink-200 bg-white">
              <ImageIcon className="h-6 w-6 text-ink-500" aria-hidden="true" />
            </span>
          )}
          <span className="min-w-0 flex-1 truncate">
            <span className="font-semibold text-ink-900">Receipt attached</span>
            <span className="ml-1 text-ink-500">— tap to open</span>
          </span>
          <ExternalLink className="h-4 w-4 shrink-0 text-ink-500" aria-hidden="true" />
        </a>
      ) : null}
    </li>
  );
}

/* ---------------------------------------------------------------------- */
/* Page                                                                   */
/* ---------------------------------------------------------------------- */

export default function PlayerLedger() {
  const { terms } = useTerms();
  const { playerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // Prefer explicit `?from=…` context (e.g. Payments row), fall back to
  // the player's own team page. Team fallback is computed after `team`
  // loads — until then we default to /coach/teams so the not-found case
  // still has somewhere sensible to go.

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/players/${playerId}/ledger`);
      setData(data);
      setNotFound(false);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      else toast.error("Couldn't load player ledger", { description: e.response?.data?.detail || "" });
    } finally {
      setLoading(false);
    }
  }, [playerId]);

  useEffect(() => { load(); }, [load]);

  // Deep-link into Payments filtered by this player + status="submitted" so
  // coach can use the existing Approve/Reject inline actions on Review.
  const goReview = useCallback((_r) => {
    navigate(`/coach/payments?status=submitted`);
  }, [navigate]);

  const { player, team, parents, totals, records } = data || {};
  // SmartBack: if we came from Payments (or anywhere else) via `?from=…`,
  // honor that. Otherwise fall back to the player's team page so coaches
  // returning from the ledger land on the roster they were browsing.
  const backDefault = team ? `/coach/teams/${team.id}` : "/coach/teams";
  const backDefaultLabel = team?.name || (terms.Teams);
  const back = useSmartBack(backDefault, backDefaultLabel);
  const activeRecords = useMemo(
    () => (records || []).filter((r) => r.status !== "canceled"),
    [records],
  );
  const canceledRecords = useMemo(
    () => (records || []).filter((r) => r.status === "canceled"),
    [records],
  );

  if (loading) {
    return (
      <AppLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-40 rounded bg-ink-100" />
          <div className="h-24 rounded-2xl bg-ink-100" />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-20 rounded-xl bg-ink-100" />)}
          </div>
          <div className="h-64 rounded-2xl bg-ink-100" />
        </div>
      </AppLayout>
    );
  }

  if (notFound) {
    return (
      <AppLayout>
        <div className="rounded-2xl border border-ink-100 bg-white p-8 text-center" data-testid="player-ledger-not-found">
          <AlertTriangle className="mx-auto h-8 w-8 text-rose-500" aria-hidden="true" />
          <h1 className="mt-3 font-heading text-2xl font-bold text-ink-900">{terms.Athlete} not found</h1>
          <p className="mt-2 text-sm text-ink-600">
            This {terms.athlete} doesn&rsquo;t exist or isn&rsquo;t on a {terms.team} you own.
          </p>
          <Link to={back.to} className="mt-4 inline-flex">
            <Button className="h-10 bg-brand font-semibold text-white hover:bg-brand-hover">
              Back to {back.label}
            </Button>
          </Link>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-5">
        <Link
          to={back.to}
          className="inline-flex items-center gap-1 text-sm font-semibold text-ink-500 hover:text-ink-900"
          data-testid="player-ledger-back"
        >
          <ArrowLeft className="h-4 w-4" /> Back to {back.label}
        </Link>

        {/* Profile header */}
        <section
          className="rounded-2xl border border-ink-100 bg-white p-5 shadow-sm"
          data-testid="player-ledger-profile"
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="font-heading text-2xl font-bold text-ink-900 sm:text-3xl">
                {player.first_name} {player.last_name}
                {player.jersey ? (
                  <span className="ml-2 align-middle text-lg font-semibold text-ink-500" data-testid="player-ledger-jersey">
                    #{player.jersey}
                  </span>
                ) : null}
                {player.archived ? (
                  <span className="ml-2 inline-block rounded-full bg-ink-100 px-2 py-0.5 align-middle text-xs font-medium text-ink-500">
                    Archived
                  </span>
                ) : null}
              </h1>
              <p className="mt-1 text-sm text-ink-600" data-testid="player-ledger-team">
                <Link to={`/coach/teams/${team.id}`} className="font-semibold text-ink-700 hover:text-brand">
                  {team.name}
                </Link>
                {team.level ? <span className="text-ink-500"> · {team.level}</span> : null}
              </p>
            </div>
          </div>

          <div className="mt-4 border-t border-ink-100 pt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-500">
              {parents.length === 1 ? "Parent / Guardian" : "Parents / Guardians"}
            </p>
            {parents.length === 0 ? (
              <p className="mt-1 text-sm text-ink-500" data-testid="player-ledger-no-parent">
                No parent linked yet.
              </p>
            ) : (
              <ul className="mt-2 space-y-1.5" data-testid="player-ledger-parents">
                {parents.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-sm">
                    <span className="font-semibold text-ink-900" data-testid={`player-ledger-parent-name-${p.id}`}>
                      {p.name || "Unnamed parent"}
                    </span>
                    <a
                      href={`mailto:${p.email}`}
                      className="inline-flex items-center gap-1 text-ink-600 hover:text-brand"
                      data-testid={`player-ledger-parent-email-${p.id}`}
                    >
                      <Mail className="h-3.5 w-3.5" aria-hidden="true" /> {p.email}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Totals strip */}
        <section className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <TotalPill
            label="Assigned"
            amount={totals.assigned}
            icon={Wallet}
            testid="player-ledger-total-assigned"
          />
          <TotalPill
            label="Paid"
            amount={totals.paid}
            icon={CheckCircle2}
            tone="paid"
            testid="player-ledger-total-paid"
          />
          <TotalPill
            label="Unpaid"
            amount={totals.unpaid}
            icon={Wallet}
            tone="unpaid"
            testid="player-ledger-total-unpaid"
          />
          <TotalPill
            label="To Review"
            amount={totals.review}
            icon={Inbox}
            tone="review"
            testid="player-ledger-total-review"
          />
          <TotalPill
            label="Overdue"
            amount={totals.overdue}
            icon={CalendarClock}
            tone="overdue"
            testid="player-ledger-total-overdue"
          />
        </section>

        {/* Active ledger */}
        <section>
          <h2 className="mb-2 font-heading text-lg font-bold text-ink-900">
            Fee ledger
            <span className="ml-2 text-sm font-normal text-ink-500">
              {activeRecords.length} {activeRecords.length === 1 ? "record" : "records"}
            </span>
          </h2>
          {activeRecords.length === 0 ? (
            <div
              className="rounded-2xl border border-dashed border-ink-200 bg-white p-8 text-center"
              data-testid="player-ledger-empty"
            >
              <p className="text-sm text-ink-500">No fees assigned to this {terms.athlete} yet.</p>
              <Link to="/coach/charges/new" className="mt-3 inline-flex">
                <Button className="h-10 bg-brand font-semibold text-white hover:bg-brand-hover">
                  Create a fee
                </Button>
              </Link>
            </div>
          ) : (
            <ul className="space-y-3">
              {activeRecords.map((r) => (
                <LedgerRow key={r.id} r={r} onReview={goReview} />
              ))}
            </ul>
          )}
        </section>

        {/* Audit trail — canceled records kept but flagged */}
        {canceledRecords.length > 0 ? (
          <section>
            <details className="rounded-2xl border border-ink-100 bg-ink-100/40 p-4" data-testid="player-ledger-canceled">
              <summary className="cursor-pointer text-sm font-semibold text-ink-700">
                Canceled fees ({canceledRecords.length}) &mdash; kept for audit, not counted in totals
              </summary>
              <ul className="mt-3 space-y-3">
                {canceledRecords.map((r) => (
                  <LedgerRow key={r.id} r={r} onReview={goReview} />
                ))}
              </ul>
            </details>
          </section>
        ) : null}
      </div>
    </AppLayout>
  );
}
